// Contrail V3.9.4 — top-right account menu (replaces the plain email + "Sign out" button on every page):
// Change password (current + new, checked against the account before saving) and Sign out.
import { FormEvent, useEffect, useRef, useState } from "react";
import { supabase } from "../lib/supabase";
import { presenceSignOut } from "../lib/presence";

const MIN = 10;   // same rule as the Set password page

export default function UserMenu({ email }: { email: string | null | undefined }) {
  const [open, setOpen] = useState(false);
  const [pwOpen, setPwOpen] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent) => { if (!ref.current?.contains(e.target as Node)) setOpen(false); };
    const esc = (e: KeyboardEvent) => e.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", close); document.addEventListener("keydown", esc);
    return () => { document.removeEventListener("mousedown", close); document.removeEventListener("keydown", esc); };
  }, [open]);
  useEffect(() => { if (!toast) return; const t = setTimeout(() => setToast(null), 4000); return () => clearTimeout(t); }, [toast]);

  return (
    <div className="umenu" ref={ref}>
      <button type="button" className="umenu-btn" aria-haspopup="menu" aria-expanded={open} onClick={() => setOpen(!open)} title="Your account">
        <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true"><circle cx="12" cy="8" r="4" /><path d="M4 21a8 8 0 0 1 16 0" /></svg>
        <span className="umenu-email mono">{email}</span>
        <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2.4" aria-hidden="true"><path d="m6 9 6 6 6-6" /></svg>
      </button>
      {open && (
        <div className="umenu-pop" role="menu">
          <div className="umenu-head"><span className="sub">Signed in as</span><b className="mono">{email}</b></div>
          <button type="button" role="menuitem" onClick={() => { setOpen(false); setPwOpen(true); }}>Change password</button>
          <button type="button" role="menuitem" className="danger" onClick={() => presenceSignOut().finally(() => supabase.auth.signOut())}>Sign out</button>
        </div>
      )}
      {pwOpen && <ChangePassword email={email ?? ""} onClose={() => setPwOpen(false)} onDone={() => { setPwOpen(false); setToast("Password changed."); }} />}
      {toast && <div className="umenu-toast" role="status">{toast}</div>}
    </div>
  );
}

export function ChangePassword({ email, onClose, onDone }: { email: string; onClose: () => void; onDone: () => void }) {
  const [cur, setCur] = useState("");
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  useEffect(() => {
    const esc = (e: KeyboardEvent) => e.key === "Escape" && !busy && onClose();
    document.addEventListener("keydown", esc);
    return () => document.removeEventListener("keydown", esc);
  }, [busy, onClose]);

  async function save(e: FormEvent) {
    e.preventDefault();
    setErr(null);
    if (!cur) return setErr("Enter your current password.");
    if (pw.length < MIN) return setErr(`Use at least ${MIN} characters for the new password.`);
    if (pw !== pw2) return setErr("The two new passwords don't match.");
    if (pw === cur) return setErr("Choose a password different from your current one.");
    setBusy(true);
    // check the current password first (this also refreshes the session, which Supabase wants before a password change)
    const check = await supabase.auth.signInWithPassword({ email, password: cur });
    if (check.error) { setBusy(false); return setErr(/invalid/i.test(check.error.message) ? "Your current password isn't right." : check.error.message); }
    const { error } = await supabase.auth.updateUser({ password: pw, data: { must_change_password: false } });
    setBusy(false);
    if (error) return setErr(/different from the old|same/i.test(error.message) ? "Choose a password you haven't used before." : error.message);
    onDone();
  }

  return (
    <div className="pw-backdrop" onMouseDown={(e) => { if (e.target === e.currentTarget && !busy) onClose(); }}>
      <form className="pw-dialog" noValidate role="dialog" aria-modal="true" aria-labelledby="pw-title" onSubmit={save}>
        <h2 id="pw-title">Change password</h2>
        <p className="sub">For <span className="mono">{email}</span></p>
        <label>Current password<input type="password" autoComplete="current-password" value={cur} onChange={(e) => setCur(e.target.value)} autoFocus /></label>
        <label>New password<input type="password" autoComplete="new-password" value={pw} onChange={(e) => setPw(e.target.value)} /></label>
        <label>Repeat new password<input type="password" autoComplete="new-password" value={pw2} onChange={(e) => setPw2(e.target.value)} /></label>
        <p className="sub">At least {MIN} characters. Forgot your current password? Sign out and use “Forgot password” on the sign-in page.</p>
        {err && <div className="error" role="alert">{err}</div>}
        <div className="pw-actions">
          <button type="button" className="btn-ghost" onClick={onClose} disabled={busy}>Cancel</button>
          <button className="btn-primary" disabled={busy}>{busy ? "Saving…" : "Change password"}</button>
        </div>
      </form>
    </div>
  );
}
